import { Activity, AlertTriangle, Radar, Scissors } from 'lucide-react'
import { useEffect } from 'react'
import { useApp } from '../context'
import { TIER_LABEL, fmtM, fmtPct } from '../format'
import type { CableTier } from '../api'
import { PanelShell } from './PanelShell'
import { Button, Callout, Section, Stat } from './ui'

export function Overview() {
  const { network, open, setHighlight } = useApp()
  const c = network.summary.counts
  useEffect(() => setHighlight({}), [setHighlight])
  const untraceable = c.services - c.traceable
  return (
    <PanelShell title="Network overview" subtitle={`${network.olts.length} OLT${network.olts.length === 1 ? '' : 's'} · ${c.nodes.toLocaleString()} sites · ${c.cables.toLocaleString()} cables · ${c.splices.toLocaleString()} splices`}>
      <Section title="Subscribers">
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Traced to an OLT" value={`${c.traceable.toLocaleString()} / ${c.services.toLocaleString()}`} sub={fmtPct(c.traceable, c.services)} tone={untraceable ? 'warn' : 'ok'} />
          <Stat label="Below design Rx" value={c.deviating} sub="more than 3 dB worse" tone={c.deviating ? 'danger' : 'ok'} />
          <Stat label="NAP ports used" value={`${c.napPortsUsed.toLocaleString()} / ${c.napPortsTotal.toLocaleString()}`} sub={fmtPct(c.napPortsUsed, c.napPortsTotal)} />
          <Stat label="Splices recorded" value={c.splices.toLocaleString()} sub={`${c.splitters.toLocaleString()} splitters`} />
        </div>
        {(untraceable > 0 || c.deviating > 0) && (
          <div className="mt-3">
            <Callout tone="warn" title={`${untraceable + c.deviating} subscriber issues need attention`}>
              <Button size="sm" className="mt-1" onClick={() => open({ kind: 'quality' })}>
                <AlertTriangle className="h-4 w-4" aria-hidden /> Open data quality
              </Button>
            </Callout>
          </div>
        )}
      </Section>
      <Section title="Cable plant">
        <table className="w-full text-sm">
          <caption className="sr-only">Route length by cable tier</caption>
          <tbody>
            {(Object.keys(TIER_LABEL) as CableTier[]).map((t) => (
              <tr key={t} className="border-b border-border last:border-0">
                <th scope="row" className="py-1.5 text-left font-normal text-muted">
                  {TIER_LABEL[t]}
                </th>
                <td className="num py-1.5 text-right">{network.summary.kmByTier[t] ? fmtM(network.summary.kmByTier[t]! * 1000) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section title="Tools">
        <div className="grid gap-2">
          <Button className="justify-start" onClick={() => open({ kind: 'otdr' })}>
            <Radar className="h-4 w-4" aria-hidden /> Locate an OTDR event
          </Button>
          <Button className="justify-start" onClick={() => open({ kind: 'quality' })}>
            <Activity className="h-4 w-4" aria-hidden /> Data quality and optical issues
          </Button>
          <p className="flex items-start gap-1.5 text-xs text-muted">
            <Scissors className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            To see who a fault affects, select a cable, closure or LCP on the map and choose “Simulate failure”.
          </p>
        </div>
      </Section>
    </PanelShell>
  )
}
